import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type CarryPlan, type Check, type DaySummary } from '@/api/client'
import { ErrorNote, Spinner, Tag, fmtDate, todayISO } from '@/components/ui'
import { useChannel } from '@/lib/channel'

/** When the day ends - the Owner's setting, not a deploy-time constant.
 *
 *  `eod_time` was always editable, but only as one raw key in an alphabetical list of
 *  twenty on the Master data screen, where nothing said which of `digest_time`,
 *  `recurring_generate_time` and `eod_time` ends the day. It belongs here, next to the
 *  button that does the closing.
 *
 *  The two settings are shown together on purpose. `eod_time` alone does NOT close the
 *  day - it runs carry-forward and the summary. The day is only signed off
 *  automatically when `auto_day_close` is on, and it defaults to off, so an Owner who
 *  set just the time would have got half of what they asked for.
 */
function WhenTheDayEnds({ channelName }: { channelName: string }) {
  const qc = useQueryClient()
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, string>>('/settings'),
  })
  const [draft, setDraft] = useState<{ at: string; auto: boolean } | null>(null)
  const [err, setErr] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: (v: { at: string; auto: boolean }) =>
      api.patch('/settings', { eod_time: v.at, auto_day_close: String(v.auto) }),
    onSuccess: () => {
      setErr(null); setDraft(null); setSaved(true)
      // The jobs are re-registered server-side on save, so nothing here needs to ask
      // for a restart - see `patch_settings`.
      ;['settings', 'day', 'checks', 'carry-preview'].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] }))
    },
    onError: setErr,
  })

  if (settings.isLoading) return null
  const live = {
    at: (settings.data?.eod_time ?? '').slice(0, 5) || '23:50',
    auto: (settings.data?.auto_day_close ?? '').toLowerCase() === 'true',
  }
  const form = draft ?? live
  const dirty = form.at !== live.at || form.auto !== live.auto

  // Ten minutes to midnight is enough on a quiet box and not much else. A missed run
  // is no longer lost work - carry-forward selects `date <= today`, so the next night
  // sweeps up what this one left - but it does slip a day, which is worth saying.
  const nearMidnight = /^23:5\d$/.test(form.at)

  return (
    <div className="card mt-4">
      <div className="card-head">
        <b className="text-[13px]">When the day ends</b>
        <span className="flex-1" />
        {saved && !dirty ? <Tag>saved · jobs rescheduled</Tag> : null}
      </div>
      <div className="p-4 space-y-3">
        {err ? <ErrorNote error={err} /> : null}
        <div className="flex gap-4 items-start">
          <div className="w-40">
            <label className="label">Time</label>
            <input className="field" type="time" value={form.at}
                   onChange={(e) => {
                     setSaved(false)
                     setDraft({ ...form, at: e.target.value })
                   }} />
          </div>
          <div className="flex-1 space-y-1.5 pt-1">
            {[[true, 'Close the day automatically at that time'],
              [false, 'Leave it to me to sign off']].map(([v, label]) => (
              <label key={String(v)} className="flex items-center gap-2 text-[12.5px]">
                <input type="radio" checked={form.auto === v}
                       onChange={() => { setSaved(false); setDraft({ ...form, auto: v as boolean }) }} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <p className="text-[11.8px] text-ink-3 leading-relaxed">
          At <b>{form.at}</b> unfinished work moves to the next working day, its
          carry-forward count goes up, and one {channelName} message goes to each
          person whose tasks moved.{' '}
          {form.auto
            ? <>The day is then signed off and locked — no further edits without
                reopening it.</>
            : <>The day stays open until you sign it off yourself, so you can still
                edit it afterwards.</>}
        </p>

        {nearMidnight && (
          <p className="text-[11.8px] text-pend-ink bg-pend-tint rounded px-2.5 py-1.5">
            That leaves only minutes before midnight. If the app is restarting or busy
            then, this run can be missed for that date — the next evening picks up
            whatever it left, so nothing is lost, but it lands a day later.
          </p>
        )}

        <div className="flex gap-2">
          <button className="btn btn-primary" disabled={!dirty || save.isPending}
                  onClick={() => save.mutate(form)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {dirty && (
            <button className="btn" onClick={() => { setDraft(null); setErr(null) }}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function DayClose() {
  const { label: channelName } = useChannel()
  const qc = useQueryClient()
  const [day, setDay] = useState(todayISO())
  const [err, setErr] = useState<unknown>(null)
  const [reason, setReason] = useState('')

  const summary = useQuery({ queryKey: ['day', day], queryFn: () => api.get<DaySummary>(`/days/${day}`) })
  const checks = useQuery({ queryKey: ['checks', day], queryFn: () => api.get<Check[]>(`/days/${day}/checks`) })
  const plan = useQuery({
    queryKey: ['carry-preview', day],
    queryFn: () => api.get<CarryPlan>(`/days/${day}/carry-forward/preview`),
  })

  const refresh = () => {
    setErr(null)
    ;['day', 'checks', 'carry-preview', 'tasks'].forEach((k) =>
      qc.invalidateQueries({ queryKey: [k] }))
  }
  const close = useMutation({ mutationFn: () => api.post(`/days/${day}/close`), onSuccess: refresh, onError: setErr })
  const reopen = useMutation({
    mutationFn: () => api.post(`/days/${day}/reopen`, { reason }),
    onSuccess: () => { setReason(''); refresh() }, onError: setErr,
  })

  if (summary.isLoading) return <Spinner />
  const s = summary.data!
  const p = plan.data

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      <div className="flex items-center gap-3 mb-4">
        <label className="label mb-0">Day</label>
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)}
               className="field w-44" />
        {s.is_closed && <Tag tone="bad">closed</Tag>}
        {!s.is_working_day && <Tag>not a working day</Tag>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-4">
        {[
          ['Completed', s.counters.completed, `of ${s.counters.total}`],
          ['Would move', p?.moving.length ?? 0, p ? `to ${fmtDate(p.target)}` : ''],
          // Said the opposite until now: "not carried — they stay in the queue".
          // True when written, and false since the Owner overruled it - see
          // `Status.carries_forward`. Waiting for approval moves with everything
          // else, and keeps ageing from when it was submitted rather than from the
          // task's date, which is what stops the move hiding a stalled approval.
          ['With approvers', s.counters.pending_approval, 'carried forward too, still ageing'],
        ].map(([label, value, sub]) => (
          <div key={String(label)} className="card p-4">
            <div className="label">{label}</div>
            <div className="mono text-3xl font-semibold tracking-tight">{value}</div>
            <div className="text-[11.8px] text-ink-3 mt-1">{sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="card-head"><b className="text-[13px]">Before closing</b></div>
          {checks.isLoading ? <Spinner /> : (checks.data ?? []).map((c, i) => (
            <div key={i} className="flex gap-2.5 px-4 py-2.5 border-b border-rule-2 text-[13px]">
              <span className={`mt-0.5 shrink-0 ${c.level === 'ok' ? 'text-done' : 'text-pend'}`}>
                {c.level === 'ok' ? '✓' : '⚠'}
              </span>
              <span className="text-ink-2">{c.message}</span>
            </div>
          ))}
          <div className="px-4 py-2.5 text-[11.8px] text-ink-3">
            These warn but never block — you may knowingly close a messy day.
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <b className="text-[13px]">Will move to {fmtDate(p?.target)}</b>
            <span className="flex-1" />
            <Tag>dry run — nothing written yet</Tag>
          </div>
          {plan.isLoading ? <Spinner /> : (p?.moving.length ? p.moving.map((m) => (
            <div key={m.ref} className="flex gap-2.5 px-4 py-2 border-b border-rule-2 text-[13px]">
              <span className="mono text-primary w-11 shrink-0">#{m.ref}</span>
              <span className="flex-1">
                {m.task}
                <span className="block text-[11.7px] text-ink-3 mt-0.5">
                  {m.company} · {m.carried === 1 ? 'first' : `${m.carried}${m.carried === 2 ? 'nd' : m.carried === 3 ? 'rd' : 'th'}`} carry-forward
                </span>
              </span>
              {/* A stray reads as ordinary work in this list - same "1st
                  carry-forward", no clue why it turned up. Say when it was dated, or
                  the Owner has no way to tell a swept-up task from today's own. */}
              {p.stranded.find((s) => s.ref === m.ref) && (
                <Tag tone="bad">
                  stranded since {fmtDate(p.stranded.find((s) => s.ref === m.ref)!.was_dated)}
                </Tag>
              )}
              {p.escalating.includes(m.ref) && <Tag tone="bad">ageing</Tag>}
            </div>
          )) : <div className="px-4 py-4 text-[13px] text-ink-3">Nothing to move.</div>)}
          {p?.warnings.map((w, i) => (
            <div key={i} className="px-4 py-2.5 border-t border-rule text-[12.3px] text-pend-ink bg-pend-tint">
              {w}
            </div>
          ))}
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head"><b className="text-[13px]">What closing does</b></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-rule-2">
          {[
            ['Moves the open work', `Date advances to ${fmtDate(p?.target)}, carry-forward count +1, one CARRIED_FORWARD event each.`],
            ['Writes the Day Log row', 'Counters plus a frozen snapshot of every task’s status, so today stays readable even if rows change later.'],
            ['Locks the day', 'Further edits to today’s tasks are refused until you reopen it — which needs a reason and is itself logged.'],
            ['Sends the summaries',
             `One ${channelName} message to each person whose tasks moved.`],
          ].map(([t, d]) => (
            <div key={t} className="p-3.5">
              <b className="block text-[12.5px] font-semibold mb-1">{t}</b>
              <span className="text-[11.8px] text-ink-3 leading-relaxed">{d}</span>
            </div>
          ))}
        </div>
      </div>

      <WhenTheDayEnds channelName={channelName} />

      <div className="mt-4 flex items-center gap-2.5">
        {s.is_closed ? (
          <>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="Why reopen? Kept in the permanent history."
                   className="field max-w-md" />
            <button className="btn btn-primary" disabled={reason.trim().length < 10 || reopen.isPending}
                    onClick={() => reopen.mutate()}>Reopen {fmtDate(day)}</button>
            <span className="text-[11.8px] text-ink-3">
              The original close is never erased — a day may read close → reopen → close.
            </span>
          </>
        ) : (
          <button className="btn btn-primary" disabled={close.isPending || !s.is_working_day}
                  onClick={() => close.mutate()}>
            {close.isPending ? 'Closing…' : `Close ${fmtDate(day)}`}
          </button>
        )}
      </div>
    </>
  )
}
